// src/pages/patient/PatientTickets.jsx
//
// /patient/tickets -- the patient's support tickets.
//
//   * search (>= 2 characters) and Status / Category / Priority are a draft
//     until Search; Reset clears them;
//   * filters, page and rows-per-page live in the URL;
//   * requests are sequenced (a slow older response never replaces a newer one);
//   * totals, pages and the summary counts come from the server only.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { readApiError } from "../../lib/apiErrors";
import { browserTimezone, formatDateTimeInZone } from "../../lib/displayTimezone";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { patientTicketsApi } from "../../services/patientTickets";
import { InlineError, classNames } from "../../components/patient/booking/bookingUi";
import ListPagination from "../../components/patient/common/ListPagination";
import { CategoryBadge, FALLBACK_VOCAB, PriorityBadge, StatusBadge } from "../../components/patient/tickets/ticketUi";

const PAGE_SIZES = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;
export const MIN_SEARCH = 2;
const FILTER_KEYS = ["search", "status", "category", "priority"];
const EMPTY = { search: "", status: "", category: "", priority: "" };

function positiveInt(value, fallback) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

function readUrl(params) {
  const filters = { ...EMPTY };
  FILTER_KEYS.forEach((key) => {
    filters[key] = params.get(key) || "";
  });
  const size = positiveInt(params.get("page_size"), DEFAULT_PAGE_SIZE);
  return { filters, page: positiveInt(params.get("page"), 1), pageSize: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE };
}

function searchProblem(value) {
  const text = String(value || "").trim();
  return text && text.length < MIN_SEARCH ? `Enter at least ${MIN_SEARCH} characters to search.` : "";
}

function Icon({ name }) {
  if (name === "plus") {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "refresh") {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "eye") {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  if (name === "ticket") {
    return (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 1 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 1 0 0-4V8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    );
  }
  return null;
}

const COLUMNS = "md:grid-cols-[1.2fr_2fr_0.9fr_0.8fr_1fr_0.9fr_1fr_auto]";

function TicketRow({ ticket, timeZone, onOpen }) {
  const href = `/patient/tickets/${encodeURIComponent(ticket.id)}`;
  return (
    <li
      onClick={() => onOpen(href)}
      data-testid="ticket-row"
      className={classNames("grid cursor-pointer grid-cols-2 gap-x-3 gap-y-1.5 rounded-2xl border border-border bg-white p-4 text-sm shadow-sm transition hover:border-primary/40 hover:bg-slate-50 md:items-center md:rounded-none md:border-0 md:border-b md:shadow-none", COLUMNS)}
    >
      <div className="col-span-2 min-w-0 md:col-span-1">
        <div className="font-semibold text-text">{ticket.ticket_number}</div>
        <div className="truncate font-mono text-[11px] text-muted" title={ticket.id}>{String(ticket.id || "").slice(0, 24)}…</div>
      </div>
      <div className="col-span-2 min-w-0 md:col-span-1">
        <div className="truncate font-semibold text-text">{ticket.subject}</div>
        {ticket.message_preview ? <div className="truncate text-xs text-muted">{ticket.message_preview}</div> : null}
      </div>
      <div><span className="sr-only md:hidden">Category: </span><CategoryBadge value={ticket.category} label={ticket.category_label} /></div>
      <div><span className="sr-only md:hidden">Priority: </span><PriorityBadge value={ticket.priority} label={ticket.priority_label} /></div>
      <div className="min-w-0">
        <div className="truncate font-semibold text-text">{ticket.owner_label}</div>
        <div className="text-xs text-muted">Assigned automatically</div>
      </div>
      <div><span className="sr-only md:hidden">Status: </span><StatusBadge value={ticket.status} label={ticket.status_label} /></div>
      <div className="text-xs text-muted"><span className="md:sr-only">Created </span>{formatDateTimeInZone(ticket.created_at, timeZone)}</div>
      <div className="text-right">
        <Link to={href} onClick={(e) => e.stopPropagation()} aria-label={`View ticket ${ticket.ticket_number}`}
          className="inline-flex rounded-xl border border-border bg-white p-2 text-primary hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
          <Icon name="eye" />
        </Link>
      </div>
    </li>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-text">{value ?? "—"}</div>
    </div>
  );
}

export default function PatientTickets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [timeZone] = useState(() => browserTimezone() || "");
  const [initial] = useState(() => readUrl(searchParams));
  const [draft, setDraft] = useState(initial.filters);
  const [applied, setApplied] = useState(initial.filters);
  const [page, setPage] = useState(initial.page);
  const [pageSize, setPageSize] = useState(initial.pageSize);
  const [searchError, setSearchError] = useState(() => searchProblem(initial.filters.search));
  const [reloadKey, setReloadKey] = useState(0);

  const list = useSequencedRequest(null);
  const options = useSequencedRequest(null);
  const runList = list.run;
  const runOptions = options.run;
  const appliedKey = JSON.stringify(applied);
  const appliedInvalid = Boolean(searchProblem(applied.search));

  useEffect(() => {
    runOptions(({ signal }) => patientTicketsApi.filterOptions({ signal }));
  }, [runOptions]);

  useEffect(() => {
    const next = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
      if (applied[key]) next.set(key, applied[key]);
    });
    if (page > 1) next.set("page", String(page));
    if (pageSize !== DEFAULT_PAGE_SIZE) next.set("page_size", String(pageSize));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // searchParams is compared, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey, page, pageSize]);

  const load = useCallback(() => {
    if (appliedInvalid) return;
    runList(({ signal }) => patientTicketsApi.list({ ...applied, page, page_size: pageSize }, { signal }));
    // appliedKey captures every applied value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runList, appliedKey, appliedInvalid, page, pageSize]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const data = list.data;
  const items = useMemo(() => data?.items || [], [data]);
  const total = Number(data?.total || 0);

  useEffect(() => {
    if (!data || list.loading) return;
    const pages = Number(data.total_pages || 0);
    if (data.total > 0 && !items.length && pages > 0 && page > pages) setPage(pages);
  }, [data, items, page, list.loading]);

  function onSearch(event) {
    event.preventDefault();
    const next = { ...draft, search: draft.search.trim() };
    const problem = searchProblem(next.search);
    setSearchError(problem);
    if (problem) {
      document.getElementById("tickets-search")?.focus();
      return;
    }
    setDraft(next);
    setApplied(next);
    setPage(1);
    setReloadKey((k) => k + 1);
  }

  function onReset() {
    setDraft(EMPTY);
    setApplied(EMPTY);
    setSearchError("");
    setPage(1);
    setReloadKey((k) => k + 1);
  }

  function onRefresh() {
    if (list.loading) return;
    setReloadKey((k) => k + 1);
  }

  const opt = options.data || {};
  const statuses = opt.statuses?.length ? opt.statuses : FALLBACK_VOCAB.statuses;
  const categories = opt.categories?.length ? opt.categories : FALLBACK_VOCAB.categories;
  const priorities = opt.priorities?.length ? opt.priorities : FALLBACK_VOCAB.priorities;
  const pageSizes = Array.isArray(opt.page_sizes) && opt.page_sizes.length ? opt.page_sizes : PAGE_SIZES;
  const summary = data?.summary;
  const selectClass = "w-full rounded-2xl border border-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div className="mx-auto w-full max-w-6xl py-2 sm:py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">ThePulseCare support</p>
          <h1 className="mt-1 text-2xl font-semibold text-text">My tickets</h1>
          <p className="mt-1 text-sm text-muted">Raise, track, and continue support conversations for appointments, invoices, and other questions</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onRefresh} disabled={list.loading} aria-busy={list.loading || undefined}
            className="inline-flex items-center gap-2 rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-slate-50 disabled:opacity-60">
            <Icon name="refresh" />
            {list.loading ? "Refreshing…" : "Refresh"}
          </button>
          <Link to="/patient/tickets/new" className="btn-primary-gradient inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white">
            <Icon name="plus" />
            Raise ticket
          </Link>
        </div>
      </div>

      {summary ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="ticket-summary">
          <SummaryTile label="Total" value={summary.total} />
          <SummaryTile label="Open" value={summary.open} />
          <SummaryTile label="In progress" value={summary.in_progress} />
          <SummaryTile label="Resolved" value={summary.resolved} />
          <SummaryTile label="Closed" value={summary.closed} />
        </div>
      ) : null}

      <form onSubmit={onSearch} noValidate role="search" aria-label="Filter tickets" className="mt-4 rounded-3xl border border-border bg-white p-4 shadow-soft sm:p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_1fr_1fr_auto]">
          <div>
            <label htmlFor="tickets-search" className="sr-only">Search tickets</label>
            <input id="tickets-search" type="search" value={draft.search} maxLength={200} placeholder="Search ticket number, subject, or message"
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, search: e.target.value }));
                if (searchError) setSearchError(searchProblem(e.target.value));
              }}
              aria-invalid={searchError ? "true" : undefined} aria-describedby={searchError ? "tickets-search-error" : undefined}
              className={classNames(selectClass, searchError && "border-rose-400")} />
            {searchError ? <p id="tickets-search-error" role="alert" className="mt-1 text-xs font-semibold text-rose-700">{searchError}</p> : null}
          </div>
          <div>
            <label htmlFor="tickets-status" className="sr-only">Status</label>
            <select id="tickets-status" value={draft.status} onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))} className={selectClass}>
              <option value="">All Status</option>
              {statuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="tickets-category" className="sr-only">Category</label>
            <select id="tickets-category" value={draft.category} onChange={(e) => setDraft((prev) => ({ ...prev, category: e.target.value }))} className={selectClass}>
              <option value="">All Categories</option>
              {categories.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="tickets-priority" className="sr-only">Priority</label>
            <select id="tickets-priority" value={draft.priority} onChange={(e) => setDraft((prev) => ({ ...prev, priority: e.target.value }))} className={selectClass}>
              <option value="">All Priority</option>
              {priorities.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary-gradient rounded-2xl px-4 py-2 text-sm font-semibold text-white">Search</button>
            <button type="button" onClick={onReset} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-slate-50">Reset</button>
          </div>
        </div>
      </form>

      <section aria-busy={list.loading || undefined} className="mt-4 rounded-3xl border border-border bg-white p-4 shadow-soft sm:p-5">
        <div className="flex items-center justify-between text-sm" aria-live="polite">
          <span className="font-semibold text-text" data-testid="ticket-count">{data ? `${total} ${total === 1 ? "ticket" : "tickets"} found` : ""}</span>
          {list.loading && data ? <span className="text-muted">Refreshing…</span> : null}
        </div>

        {list.error ? <div className="mt-3"><InlineError message={readApiError(list.error, "Tickets could not be loaded.")} onRetry={onRefresh} /></div> : null}

        {list.loading && !data ? (
          <ul className="mt-3 space-y-2" aria-label="Loading tickets">
            {[0, 1, 2].map((n) => <li key={n} className="h-16 animate-pulse rounded-2xl border border-border bg-slate-50" />)}
          </ul>
        ) : null}

        {data && !items.length && !(total > 0) ? (
          <div className="mt-3 flex flex-col items-center rounded-3xl border border-dashed border-border p-8 text-center" data-testid="tickets-empty">
            <span className="rounded-2xl bg-primary/10 p-3 text-primary"><Icon name="ticket" /></span>
            <p className="mt-3 text-sm font-semibold text-text">No tickets found</p>
            <p className="mt-1 text-sm text-muted">Try changing the search or filters, or raise a new ticket.</p>
          </div>
        ) : null}

        {items.length ? (
          <>
            <div className={classNames("mt-3 hidden gap-x-3 border-b border-border px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-muted md:grid", COLUMNS)} aria-hidden="true">
              <span>Ticket</span><span>Subject</span><span>Category</span><span>Priority</span><span>Owner</span><span>Status</span><span>Created</span><span className="sr-only">View</span>
            </div>
            <ul className={classNames("mt-3 space-y-2 md:mt-0 md:space-y-0", list.loading && "opacity-80")} aria-label="Tickets">
              {items.map((ticket) => <TicketRow key={ticket.id} ticket={ticket} timeZone={timeZone} onOpen={navigate} />)}
            </ul>
          </>
        ) : null}

        {data && total > 0 ? (
          <ListPagination idPrefix="tickets" page={data.page || page} totalPages={data.total_pages} total={total} noun="total tickets" pageSize={pageSize} pageSizes={pageSizes}
            disabled={list.loading} onPage={(p) => setPage(p)} onPageSize={(size) => { setPageSize(size); setPage(1); }} />
        ) : null}
      </section>
    </div>
  );
}
